import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveRestore, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "../../api/client";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { unwrapArray } from "../../lib/api-envelope";
import { formatCalendarDate, todayCalendarDate } from "../../lib/calendar-date";
import { fmtCurrency } from "../../lib/display-labels";
import { BILLING, BILLING_TYPES, isBillingType } from "../../lib/load-rates";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { LOAD_TYPE_OPTIONS, getLoadTypeLabel } from "../../constants/load-types";
import { useFleetUnits } from "../../screens/trucking/FleetRegistryScreen";
import { TRUCKING_RATE_KINDS, type TruckingRate, type TruckingRateKind, type TruckingRateWrite } from "../../types/trucking-rates";

/**
 * /trucking/rates — the trucking rate card (api#950, `trucking_rates` 0474).
 *
 * WHAT A RATE IS, IN THE API'S TERMS. One row is one KIND (haul, wait time,
 * fuel surcharge, carrier passthrough) for a SCOPE (a customer, a truck, both,
 * or the whole company when both are empty) over a range of calendar days, both
 * ends inclusive; no end date is open-ended. A new load takes each kind from
 * the most specific live row: truck + customer → customer → truck → company,
 * and within one scope a row for the load's own type beats one for any type.
 * That choice is the API's (resolveTruckingRate); the page never re-derives it.
 *
 * Each kind carries only its own fields. The API checks the shape and answers
 * in plain English (400 TRUCKING_RATE_INVALID); two live rows for one key may
 * not cover the same day (409 TRUCKING_RATE_OVERLAP, naming the row in the
 * way, which the page highlights). Every refusal is shown as the API words it.
 * Card values must be greater than 0: a typed 0 goes to the API, which refuses it.
 *
 * Currency is the company's: the API fills it on create, so the form never asks.
 *
 * ROLES: read admin, office_staff, supervisor, dispatcher; write admin and
 * office_staff (TRUCKING_RATE_READ_ROLES / _WRITE_ROLES in the API route).
 *
 * No stat strip: nothing here is a figure the API returns, and a count of rows
 * per kind would be a tally pretending to be one (Tool Rates is the same).
 */

const READ_ROLES = ["admin", "office_staff", "supervisor", "dispatcher"];
const WRITE_ROLES = ["admin", "office_staff"];

const KIND: Record<TruckingRateKind, { title: string; empty: string }> = {
  haul: { title: "Haul", empty: "No haul rates. A load with no haul rate cannot be invoiced." },
  wait_time: { title: "Wait time", empty: "No wait-time rates." },
  fuel_surcharge: { title: "Fuel surcharge", empty: "No fuel surcharges." },
  carrier_passthrough: { title: "Carrier passthrough", empty: "No carrier passthrough markups." },
};

const INPUT =
  "h-10 w-full min-w-0 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none";
const SECONDARY =
  "inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50 disabled:opacity-50";
const PRIMARY = "fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50";
const DANGER =
  "inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:opacity-50";

const n = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));
const pct = (v: unknown) => (n(v) === null || !Number.isFinite(n(v)) ? "—" : `${n(v)}%`);
const inEffect = (r: TruckingRate) =>
  `${formatCalendarDate(r.effective_date) ?? "—"} – ${r.expiry_date ? formatCalendarDate(r.expiry_date) ?? "—" : "onward"}`;

function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-fieldiq-muted">{hint}</p>}
    </div>
  );
}

function Dialog({ title, onClose, footer, children }: { title: string; onClose: () => void; footer: React.ReactNode; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title} className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
        <header className="border-b border-fieldiq-border px-5 py-4">
          <h2 className="text-base font-extrabold tracking-tight text-fieldiq-text">{title}</h2>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <footer className="flex flex-wrap justify-end gap-2 border-t border-fieldiq-border px-5 py-3">{footer}</footer>
      </div>
    </div>
  );
}

type Column = { label: string; align?: "right"; cell: (r: TruckingRate) => React.ReactNode };

export default function TruckingRatesScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role ?? "";
  const canRead = READ_ROLES.includes(role);
  const canWrite = WRITE_ROLES.includes(role);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<TruckingRate | TruckingRateKind | null>(null);
  const [deleting, setDeleting] = useState<TruckingRate | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["trucking-rates", showDeleted],
    queryFn: async () => (await api.trucking.rates.list(showDeleted ? { include_deleted: "true" } : undefined)).data.data ?? [],
    enabled: canRead,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const customers = useQuery({
    queryKey: ["customers"],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    enabled: canRead,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  // Retired units included: a rate on a unit since retired still names it.
  const fleet = useFleetUnits(true);
  const customerName = (id: string | null) => (id ? customers.data?.find((c) => c.id === id)?.name ?? "—" : null);
  const truckName = (id: string | null) => (id ? fleet.data?.find((u) => u.id === id)?.unit_number ?? "—" : null);
  const scope = (r: TruckingRate) => {
    const c = customerName(r.customer_id);
    const t = truckName(r.truck_id);
    if (!c && !t) return <span className="text-fieldiq-muted">Company-wide</span>;
    return [c, t ? `Truck ${t}` : null].filter(Boolean).join(" · ");
  };

  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];

  const restore = useMutation({
    mutationFn: (id: string) => api.trucking.rates.restore(id),
    onSuccess: () => {
      toast.success("Rate restored");
      qc.invalidateQueries({ queryKey: ["trucking-rates"] });
    },
  });
  const doRestore = async (r: TruckingRate) => {
    setNotice(null);
    try {
      await restore.mutateAsync(r.id);
    } catch (error) {
      setNotice(getApiErrorMessage(error, "The rate was not restored."));
      const id = (error as { response?: { data?: { conflicting_rate_id?: string } } })?.response?.data?.conflicting_rate_id;
      if (id) setHighlight(id);
    }
  };

  const loadTypeCell = (r: TruckingRate) => (r.load_type ? getLoadTypeLabel(r.load_type) : <span className="text-fieldiq-muted">Any load type</span>);
  const COLUMNS: Record<TruckingRateKind, Column[]> = {
    haul: [
      { label: "Load type", cell: loadTypeCell },
      { label: "Billing", cell: (r) => (isBillingType(r.billing_type) ? BILLING[r.billing_type].label : "—") },
      { label: "Rate", align: "right", cell: (r) => fmtCurrency(r.rate, r.currency) },
    ],
    wait_time: [
      { label: "Load type", cell: loadTypeCell },
      { label: "Per hour", align: "right", cell: (r) => fmtCurrency(r.hourly_rate, r.currency) },
      { label: "Free minutes", align: "right", cell: (r) => (r.free_wait_minutes == null ? "—" : String(r.free_wait_minutes)) },
    ],
    fuel_surcharge: [
      { label: "Load type", cell: loadTypeCell },
      {
        label: "Surcharge",
        align: "right",
        cell: (r) => (r.fuel_basis === "fixed" ? fmtCurrency(r.fuel_amount, r.currency) : r.fuel_basis === "percent" ? `${pct(r.fuel_percent)} of haul` : "—"),
      },
    ],
    carrier_passthrough: [{ label: "Markup", align: "right", cell: (r) => pct(r.markup_percent) }],
  };

  if (!canRead) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Trucking" title="Trucking Rates" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <p className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center text-sm text-fieldiq-text">
            Rates are visible to admin, office staff, supervisors and dispatchers.
          </p>
        </section>
      </div>
    );
  }

  const actionsCell = (r: TruckingRate) =>
    r.deleted_at ? (
      <button type="button" className={SECONDARY} disabled={restore.isPending} onClick={() => doRestore(r)}>
        <ArchiveRestore className="size-4" aria-hidden="true" />
        Restore
      </button>
    ) : (
      <span className="inline-flex gap-2">
        <button type="button" className={SECONDARY} onClick={() => { setNotice(null); setEditing(r); }} aria-label={`Edit ${KIND[r.kind as TruckingRateKind]?.title ?? ""} rate`}>
          <Pencil className="size-4" aria-hidden="true" />
          Edit
        </button>
        <button type="button" className={SECONDARY} onClick={() => setDeleting(r)} aria-label={`Delete ${KIND[r.kind as TruckingRateKind]?.title ?? ""} rate`}>
          <Trash2 className="size-4" aria-hidden="true" />
        </button>
      </span>
    );

  return (
    <div data-testid="trucking-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Trucking Rates"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite && query.data ? (
            <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setNotice(null); setEditing("haul"); }}>
              <Plus className="size-4" aria-hidden="true" />
              Add rate
            </button>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-fieldiq-muted">A load uses the most specific rate in effect on its day.</p>
            <label className="flex items-center gap-2 text-sm text-fieldiq-text">
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
          </div>

          {notice && (
            <p role="alert" data-testid="rates-notice" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
              {notice}
            </p>
          )}

          {failure ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="rates" />
            </div>
          ) : query.isLoading || !query.data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading rates" />
            </div>
          ) : (
            TRUCKING_RATE_KINDS.map((kind) => {
              const list = rows.filter((r) => r.kind === kind);
              const cols: Column[] = [{ label: "Applies to", cell: scope }, ...COLUMNS[kind], { label: "In effect", cell: inEffect }];
              return (
                <section
                  key={kind}
                  data-testid={`rates-${kind}`}
                  aria-labelledby={`rates-${kind}-title`}
                  className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
                >
                  <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
                    <div>
                      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Rate card</p>
                      <h2 id={`rates-${kind}-title`} className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">{KIND[kind].title}</h2>
                    </div>
                    {canWrite && (
                      <button type="button" className={SECONDARY} onClick={() => { setNotice(null); setEditing(kind); }}>
                        <Plus className="size-4" aria-hidden="true" />
                        Add {KIND[kind].title.toLowerCase()} rate
                      </button>
                    )}
                  </header>
                  {list.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-fieldiq-text">{KIND[kind].empty}</p>
                  ) : (
                    <>
                      <div className="hidden overflow-x-auto md:block">
                        <table className="w-full min-w-[860px] text-left">
                          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            <tr className="border-l-4 border-l-transparent">
                              {cols.map((c) => (
                                <th key={c.label} scope="col" className={`px-4 py-3 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                              ))}
                              {canWrite && <th scope="col" className="px-4 py-3 text-right">Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {list.map((r) => (
                              <tr
                                key={r.id}
                                data-testid="rate-row"
                                data-rate-id={r.id}
                                className={`border-t border-l-4 border-fieldiq-border border-l-transparent ${highlight === r.id ? "bg-fi-amber/10" : ""}`}
                              >
                                {cols.map((c, i) => (
                                  <td key={c.label} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === "right" ? "whitespace-nowrap text-right font-mono" : ""}`}>
                                    {c.cell(r)}
                                    {i === 0 && r.deleted_at && (
                                      <span className="ml-2 rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">Deleted</span>
                                    )}
                                  </td>
                                ))}
                                {canWrite && <td className="whitespace-nowrap px-4 py-3 text-right">{actionsCell(r)}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <ul className="divide-y divide-fieldiq-border md:hidden">
                        {list.map((r) => (
                          <li key={r.id} data-testid="rate-card" className={`space-y-2 px-4 py-3 ${highlight === r.id ? "bg-fi-amber/10" : ""}`}>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              {cols.map((c) => (
                                <div key={c.label} className="min-w-0">
                                  <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">{c.label}</dt>
                                  <dd className="text-fieldiq-text">{c.cell(r)}</dd>
                                </div>
                              ))}
                            </dl>
                            {r.deleted_at && (
                              <span className="inline-block rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">Deleted</span>
                            )}
                            {canWrite && <div>{actionsCell(r)}</div>}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {list.length > 0 && (
                    <footer className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                      {list.length} {list.length === 1 ? "rate" : "rates"}{showDeleted ? ", deleted included" : ""}
                    </footer>
                  )}
                </section>
              );
            })
          )}
        </div>
      </section>

      {canWrite && editing && (
        <RateDialog
          rate={typeof editing === "string" ? null : editing}
          initialKind={typeof editing === "string" ? editing : (editing.kind as TruckingRateKind)}
          customers={customers.data ?? []}
          trucks={(fleet.data ?? []).map((u) => ({ id: u.id, label: u.unit_number ?? "—" }))}
          onClose={() => setEditing(null)}
          onConflict={(id) => { setHighlight(id); setShowDeleted(false); }}
        />
      )}
      {canWrite && deleting && <DeleteDialog rate={deleting} describe={scope} onClose={() => setDeleting(null)} />}
    </div>
  );
}

type Values = Record<string, string>;
const text = (v: unknown) => (v === null || v === undefined ? "" : String(v));

function valuesOf(r: TruckingRate | null, kind: TruckingRateKind, today: string | null): Values {
  return {
    kind,
    customer_id: text(r?.customer_id),
    truck_id: text(r?.truck_id),
    load_type: text(r?.load_type),
    billing_type: text(r?.billing_type) || (kind === "haul" ? "per_load" : ""),
    rate: text(r?.rate),
    hourly_rate: text(r?.hourly_rate),
    free_wait_minutes: text(r?.free_wait_minutes),
    fuel_basis: text(r?.fuel_basis) || (kind === "fuel_surcharge" ? "fixed" : ""),
    fuel_value: r?.fuel_basis === "percent" ? text(r?.fuel_percent) : text(r?.fuel_amount),
    markup_percent: text(r?.markup_percent),
    effective_date: r?.effective_date ?? today ?? "",
    expiry_date: text(r?.expiry_date),
    notes: text(r?.notes),
  };
}

/**
 * The write body. A kind sends only its own fields. An edit sends only what
 * changed; a cleared optional box sends null. Numbers that do not parse are
 * the caller's to refuse; everything else (required fields, > 0, overlap) is
 * the API's to answer, in its own words.
 */
export function rateBody(v: Values, initial: Values | null): TruckingRateWrite | string {
  const kind = v.kind as TruckingRateKind;
  const body: Record<string, unknown> = {};
  const changed = (k: string) => !initial || v[k] !== initial[k];
  const num = (label: string, raw: string, int = false): number | null | string => {
    const t = raw.trim();
    if (t === "") return null;
    const x = Number(t);
    if (!Number.isFinite(x) || (int && !Number.isInteger(x))) return `${label} must be ${int ? "a whole number" : "a number"}.`;
    return x;
  };
  const put = (key: string, value: unknown) => { if (changed(key) || key === "kind") body[key] = value; };

  if (!initial) body.kind = kind;
  put("customer_id", v.customer_id || null);
  put("truck_id", v.truck_id || null);
  if (kind !== "carrier_passthrough") put("load_type", v.load_type || null);
  if (kind === "haul") {
    put("billing_type", v.billing_type || null);
    const r = num("Rate", v.rate); if (typeof r === "string") return r; put("rate", r);
  }
  if (kind === "wait_time") {
    const h = num("Per hour", v.hourly_rate); if (typeof h === "string") return h; put("hourly_rate", h);
    const m = num("Free minutes", v.free_wait_minutes, true); if (typeof m === "string") return m; put("free_wait_minutes", m);
  }
  if (kind === "fuel_surcharge" && (changed("fuel_basis") || changed("fuel_value"))) {
    const f = num(v.fuel_basis === "percent" ? "Surcharge %" : "Surcharge", v.fuel_value); if (typeof f === "string") return f;
    body.fuel_basis = v.fuel_basis || null;
    body.fuel_amount = v.fuel_basis === "fixed" ? f : null;
    body.fuel_percent = v.fuel_basis === "percent" ? f : null;
  }
  if (kind === "carrier_passthrough") {
    const m = num("Markup %", v.markup_percent); if (typeof m === "string") return m; put("markup_percent", m);
  }
  if (changed("effective_date")) body.effective_date = v.effective_date;
  if (changed("expiry_date")) body.expiry_date = v.expiry_date || null;
  if (changed("notes")) body.notes = v.notes.trim() || null;
  // A new rate leaves out what was never filled in, so the API names what is missing.
  if (!initial) for (const k of Object.keys(body)) if (body[k] === null && k !== "kind") delete body[k];
  return body as TruckingRateWrite;
}

function RateDialog({
  rate, initialKind, customers, trucks, onClose, onConflict,
}: {
  rate: TruckingRate | null;
  initialKind: TruckingRateKind;
  customers: { id: string; name: string }[];
  trucks: { id: string; label: string }[];
  onClose: () => void;
  onConflict: (id: string) => void;
}) {
  const qc = useQueryClient();
  const timeZone = useTenantTimeZone();
  const editing = !!rate;
  const initial = useMemo(() => valuesOf(rate, initialKind, todayCalendarDate(timeZone)), [rate, initialKind, timeZone]);
  const [v, setV] = useState<Values>(initial);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kind = v.kind as TruckingRateKind;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((all) => ({ ...all, [k]: e.target.value }));
  const id = (k: string) => `trate-${k}`;

  const submit = async () => {
    setRefusal(null);
    const body = rateBody(v, editing ? initial : null);
    if (typeof body === "string") { setRefusal(body); return; }
    if (editing && Object.keys(body).length === 0) { onClose(); return; }
    setBusy(true);
    try {
      if (rate) await api.trucking.rates.update(rate.id, body);
      else await api.trucking.rates.create(body);
      toast.success(editing ? "Rate saved" : "Rate added");
      qc.invalidateQueries({ queryKey: ["trucking-rates"] });
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, editing ? "The rate was not saved." : "The rate was not added."));
      const data = (error as { response?: { data?: { code?: string; conflicting_rate_id?: string } } })?.response?.data;
      if (data?.code === "TRUCKING_RATE_OVERLAP" && data.conflicting_rate_id) onConflict(data.conflicting_rate_id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={editing ? `Edit ${KIND[kind].title.toLowerCase()} rate` : "Add rate"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" className={PRIMARY} disabled={busy} onClick={submit}>
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {editing ? "Save rate" : "Add rate"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {!editing && (
          <Field label="Kind" htmlFor={id("kind")}>
            <select id={id("kind")} className={INPUT} value={v.kind} onChange={(e) => setV(valuesOf(null, e.target.value as TruckingRateKind, v.effective_date || null))}>
              {TRUCKING_RATE_KINDS.map((k) => <option key={k} value={k}>{KIND[k].title}</option>)}
            </select>
          </Field>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer" htmlFor={id("customer")}>
            <select id={id("customer")} className={INPUT} value={v.customer_id} onChange={set("customer_id")}>
              <option value="">Every customer</option>
              {v.customer_id && !customers.some((c) => c.id === v.customer_id) && <option value={v.customer_id}>Current customer</option>}
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Truck" htmlFor={id("truck")}>
            <select id={id("truck")} className={INPUT} value={v.truck_id} onChange={set("truck_id")}>
              <option value="">Every truck</option>
              {v.truck_id && !trucks.some((t) => t.id === v.truck_id) && <option value={v.truck_id}>Current truck</option>}
              {trucks.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {kind !== "carrier_passthrough" && (
            <Field label="Load type" htmlFor={id("load-type")}>
              <select id={id("load-type")} className={INPUT} value={v.load_type} onChange={set("load_type")}>
                <option value="">{kind === "haul" ? "Choose…" : "Any load type"}</option>
                {LOAD_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          )}
          {kind === "haul" && (
            <>
              <Field label="Billing" htmlFor={id("billing")}>
                <select id={id("billing")} className={INPUT} value={v.billing_type} onChange={set("billing_type")}>
                  {BILLING_TYPES.map((t) => <option key={t} value={t}>{BILLING[t].label}</option>)}
                </select>
              </Field>
              <Field label="Rate" htmlFor={id("rate")}>
                <input id={id("rate")} inputMode="decimal" className={INPUT} value={v.rate} onChange={set("rate")} />
              </Field>
            </>
          )}
          {kind === "wait_time" && (
            <>
              <Field label="Per hour" htmlFor={id("hourly")}>
                <input id={id("hourly")} inputMode="decimal" className={INPUT} value={v.hourly_rate} onChange={set("hourly_rate")} />
              </Field>
              <Field label="Free minutes" htmlFor={id("free")}>
                <input id={id("free")} inputMode="numeric" className={INPUT} value={v.free_wait_minutes} onChange={set("free_wait_minutes")} />
              </Field>
            </>
          )}
          {kind === "fuel_surcharge" && (
            <>
              <Field label="Surcharge type" htmlFor={id("fuel-basis")}>
                <select id={id("fuel-basis")} className={INPUT} value={v.fuel_basis} onChange={set("fuel_basis")}>
                  <option value="fixed">Fixed amount</option>
                  <option value="percent">Percent of haul</option>
                </select>
              </Field>
              <Field label={v.fuel_basis === "percent" ? "Surcharge %" : "Surcharge"} htmlFor={id("fuel-value")}>
                <input id={id("fuel-value")} inputMode="decimal" className={INPUT} value={v.fuel_value} onChange={set("fuel_value")} />
              </Field>
            </>
          )}
          {kind === "carrier_passthrough" && (
            <Field label="Markup %" htmlFor={id("markup")}>
              <input id={id("markup")} inputMode="decimal" className={INPUT} value={v.markup_percent} onChange={set("markup_percent")} />
            </Field>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From" htmlFor={id("from")}>
            <input id={id("from")} type="date" className={INPUT} value={v.effective_date} onChange={set("effective_date")} />
          </Field>
          <Field label="Until" hint="Blank: no end date." htmlFor={id("until")}>
            <input id={id("until")} type="date" className={INPUT} value={v.expiry_date} onChange={set("expiry_date")} />
          </Field>
        </div>
        <Field label="Notes" htmlFor={id("notes")}>
          <textarea id={id("notes")} className={`${INPUT} h-20 py-2`} value={v.notes} onChange={set("notes")} />
        </Field>
        {refusal && <p role="alert" data-testid="rate-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
      </div>
    </Dialog>
  );
}

function DeleteDialog({ rate, describe, onClose }: { rate: TruckingRate; describe: (r: TruckingRate) => React.ReactNode; onClose: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setRefusal(null);
    try {
      await api.trucking.rates.remove(rate.id);
      toast.success("Rate deleted");
      qc.invalidateQueries({ queryKey: ["trucking-rates"] });
      onClose();
    } catch (error) {
      setRefusal(getApiErrorMessage(error, "The rate was not deleted."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title="Delete this rate?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" className={DANGER} disabled={busy} onClick={submit}>Delete rate</button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        {KIND[rate.kind as TruckingRateKind]?.title ?? "—"} rate, {describe(rate)}, {inEffect(rate)}. New loads stop using it; it can be restored.
      </p>
      {refusal && <p role="alert" className="mt-3 text-sm font-medium text-fieldiq-text">{refusal}</p>}
    </Dialog>
  );
}
